import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Edit2, X, Save, Briefcase, DollarSign, User, Building2, Lightbulb, Search } from 'lucide-react';
import { apiCall } from '../services/api';
import { quickNotesMockData } from '../data/mockData';

const CATEGORIES = [
  { id: 'all', name: 'All Notes', icon: null },
  { id: 'personal', name: 'Personal', icon: User },
  { id: 'investments', name: 'Investments', icon: DollarSign },
  { id: 'business', name: 'Business', icon: Briefcase }
];

const SimpleNotes = () => {
  const [notes, setNotes] = useState([]);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [category, setCategory] = useState('personal');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [editingNote, setEditingNote] = useState(null);

  const loadNotes = async () => {
    try {
      // USING MOCKUP DATA - Don't fetch from API
      console.log('📋 Using mockup notes data');
      setNotes(quickNotesMockData);
      // const data = await apiCall('/notes/');
      // setNotes(data || []);
    } catch (error) {
      console.error('Error loading notes:', error);
    }
  };

  useEffect(() => {
    loadNotes();
  }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    if (!title.trim()) {
      alert('Please enter a title');
      return;
    }

    setLoading(true);
    try {
      if (editingNote) {
        // Update existing note (mockup mode)
        const updatedNotes = notes.map(note => 
          note.id === editingNote.id 
            ? { ...note, title: title.trim(), content: content.trim(), tags: [category], updated_at: new Date().toISOString() }
            : note
        );
        setNotes(updatedNotes);
        alert('✅ Note updated successfully!');
      } else {
        // Create new note (mockup mode)
        const newNote = {
          id: `note-${Date.now()}`,
          title: title.trim(),
          content: content.trim(),
          tags: [category],
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        };
        setNotes([newNote, ...notes]);
        alert('✅ Note saved successfully!');
      }

      setTitle('');
      setContent('');
      setCategory('personal');
      setEditingNote(null);
    } catch (error) {
      console.error('Error saving note:', error);
      alert('❌ Error saving note');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (note) => {
    setEditingNote(note);
    setTitle(note.title);
    setContent(note.content);
    setCategory(note.tags && note.tags.length > 0 ? note.tags[0] : 'personal');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCancelEdit = () => {
    setEditingNote(null);
    setTitle('');
    setContent('');
    setCategory('personal');
  };

  const handleDelete = async (noteId) => {
    if (!window.confirm('Delete this note?')) return;

    try {
      // Delete note (mockup mode)
      const updatedNotes = notes.filter(note => note.id !== noteId);
      setNotes(updatedNotes);
      alert('✅ Note deleted!');
    } catch (error) {
      console.error('Error deleting note:', error);
      alert('❌ Error deleting note');
    }
  };

  // Filter notes by selected category and search query
  const filteredNotes = notes
    .filter(note => {
      // Filter by category
      if (selectedCategory !== 'all') {
        return note.tags && note.tags.includes(selectedCategory);
      }
      return true;
    })
    .filter(note => {
      // Filter by search query
      if (searchQuery.trim() === '') return true;
      const query = searchQuery.toLowerCase();
      return (
        note.title.toLowerCase().includes(query) ||
        note.content.toLowerCase().includes(query)
      );
    });

  // Count notes by category (includes search filtering)
  const getCategoryCount = (categoryId) => {
    let categoryNotes = categoryId === 'all' 
      ? notes 
      : notes.filter(note => note.tags && note.tags.includes(categoryId));
    
    // Apply search filter to count
    if (searchQuery.trim() !== '') {
      const query = searchQuery.toLowerCase();
      categoryNotes = categoryNotes.filter(note =>
        note.title.toLowerCase().includes(query) ||
        note.content.toLowerCase().includes(query)
      );
    }
    
    return categoryNotes.length;
  };

  return (
    <div className="p-8 min-h-screen">
      <div className="max-w-6xl mx-auto">
        <h1 className="text-4xl font-bold text-white mb-8">Quick Notes</h1>

        {/* Create/Edit Note Form */}
        <div className="bg-transparent border border-white/60 rounded-xl p-6 mb-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-white">
              {editingNote ? 'Edit Note' : 'Create New Note'}
            </h2>
            {editingNote && (
              <button
                onClick={handleCancelEdit}
                className="flex items-center gap-2 px-3 py-1 text-white hover:text-white hover:bg-white/10 rounded-lg transition-colors"
              >
                <X className="w-4 h-4" />
                Cancel
              </button>
            )}
          </div>
          <form onSubmit={handleSave} className="space-y-4">
            <div>
              <label className="block text-white mb-2">Category</label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="w-full px-4 py-2 bg-transparent border border-white/60 rounded-lg text-white focus:outline-none focus:border-blue-500"
                disabled={loading}
              >
                {CATEGORIES.filter(c => c.id !== 'all').map((cat) => (
                  <option key={cat.id} value={cat.id} className="bg-transparent border border-white/60">
                    {cat.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-white mb-2">Title</label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Enter note title..."
                className="w-full px-4 py-2 bg-transparent border border-white/60 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-blue-500"
                disabled={loading}
              />
            </div>
            <div>
              <label className="block text-white mb-2">Content</label>
              <textarea
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder="Write your note here..."
                rows={6}
                className="w-full px-4 py-2 bg-transparent border border-white/60 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-blue-500"
                disabled={loading}
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="flex items-center gap-2 px-6 py-2 bg-white hover:bg-white text-black rounded-lg transition-colors disabled:opacity-50"
            >
              {editingNote ? <Save className="w-5 h-5" /> : <Plus className="w-5 h-5" />}
              {loading ? 'Saving...' : editingNote ? 'Update Note' : 'Save Note'}
            </button>
          </form>
        </div>

        {/* Category Tabs */}
        <div className="bg-transparent border border-white/60 rounded-xl p-4 mb-6">
          <div className="flex flex-wrap gap-2">
            {CATEGORIES.map((cat) => {
              const Icon = cat.icon;
              const count = getCategoryCount(cat.id);
              return (
                <button
                  key={cat.id}
                  onClick={() => setSelectedCategory(cat.id)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                    selectedCategory === cat.id
                      ? 'bg-white text-black'
                      : 'bg-transparent text-white hover:bg-white/10'
                  }`}
                >
                  {Icon && <Icon className="w-4 h-4" />}
                  <span>{cat.name}</span>
                  <span className="text-xs opacity-70">({count})</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Notes List */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-2xl font-semibold text-white">
              {selectedCategory === 'all' 
                ? `All Notes (${filteredNotes.length})` 
                : `${CATEGORIES.find(c => c.id === selectedCategory)?.name} Notes (${filteredNotes.length})`}
            </h2>
          </div>

          {/* Search Bar */}
          <div className="mb-4">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search notes by title or content..."
                className="w-full pl-12 pr-4 py-3 bg-transparent border border-white/60 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-blue-500"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white"
                >
                  <X className="w-5 h-5" />
                </button>
              )}
            </div>
          </div>

          {filteredNotes.length === 0 ? (
            <div className="text-center py-12 text-white bg-transparent border border-white/60 rounded-xl">
              {searchQuery ? (
                <>
                  <p className="text-lg mb-2">No notes found matching "{searchQuery}"</p>
                  <button
                    onClick={() => setSearchQuery('')}
                    className="text-blue-400 hover:text-blue-300 underline"
                  >
                    Clear search
                  </button>
                </>
              ) : selectedCategory === 'all' ? (
                'No notes yet. Create your first note above!'
              ) : (
                `No ${CATEGORIES.find(c => c.id === selectedCategory)?.name} notes yet.`
              )}
            </div>
          ) : (
            <div className="overflow-y-auto space-y-4" style={{ maxHeight: '650px' }}>
              {filteredNotes.map((note) => {
                const noteCat = CATEGORIES.find(c => c.id === (note.tags && note.tags[0]));
                const CategoryIcon = noteCat?.icon;
                
                return (
                  <div
                    key={note.id}
                    className="bg-transparent border border-white/60 rounded-xl p-6 hover:bg-white/10 transition-colors"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          {CategoryIcon && (
                            <div className="flex items-center gap-1 px-2 py-1 bg-transparent border border-white/60 text-white text-xs rounded">
                              <CategoryIcon className="w-3 h-3" />
                              <span>{noteCat?.name}</span>
                            </div>
                          )}
                        </div>
                        <h3 className="text-xl font-semibold text-white mb-2">
                          {note.title}
                        </h3>
                        <p className="text-white whitespace-pre-wrap">
                          {note.content}
                        </p>
                        <p className="text-xs text-gray-500 mt-3">
                          {new Date(note.created_at).toLocaleString()}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleEdit(note)}
                          className="p-2 text-white hover:text-gray-200 hover:bg-white/10 rounded-lg transition-colors"
                        >
                          <Edit2 className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => handleDelete(note.id)}
                          className="p-2 text-red-400 hover:text-red-300 hover:bg-red-400/10 rounded-lg transition-colors"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SimpleNotes;
